import jsPDF from 'jspdf';
import { BUSINESS_CONFIG } from '../utils/businessConfig';
import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export const generateInvoice = async (saleData, userId) => {
  const doc = new jsPDF();
  
  // Set font and encoding for better text rendering
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  
  // Header Section - Business Details
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(0, 0, 0);
  doc.text(BUSINESS_CONFIG.name, 20, 25);
  
  // Business Address and Details
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(BUSINESS_CONFIG.address.line1, 20, 35);
  doc.text(BUSINESS_CONFIG.address.line2, 20, 42);
  doc.text(`Phone: ${BUSINESS_CONFIG.address.phone}`, 20, 49);
  doc.text(`Email: ${BUSINESS_CONFIG.address.email}`, 20, 56);
  doc.text(`GST No: ${BUSINESS_CONFIG.gst.number}`, 20, 63);
  doc.text(`HSN Code: ${BUSINESS_CONFIG.gst.hsnCode}`, 20, 70);
  
  // Invoice Title and Details Box
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text('TAX INVOICE', 140, 25);
  
  // Invoice Details Box
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.rect(135, 30, 60, 30);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(`Invoice No: ${saleData.invoiceNumber || 'N/A'}`, 140, 38);
  doc.text(`Date: ${new Date(saleData.saleDate).toLocaleDateString('en-IN')}`, 140, 45);
  
  // Customer Details Section
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Bill To:', 20, 85);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(saleData.customerName || 'Cash Customer', 20, 93);
  if (saleData.customerPhone) {
    doc.text(`Phone: ${saleData.customerPhone}`, 20, 100);
  }
  if (saleData.customerEmail) {
    doc.text(`Email: ${saleData.customerEmail}`, 20, saleData.customerPhone ? 107 : 100);
  }
  
  // Calculate amounts first - TRANSPARENT GST CALCULATION
  const baseAmount = parseFloat(saleData.saleAmount) || 0;
  const discount = parseFloat(saleData.discount) || 0;
  const amountAfterDiscount = baseAmount - discount;
  
  // Calculate GST on the discounted amount (transparent billing)
  const gstRate = 18.0;
  const gstAmount = (amountAfterDiscount * gstRate) / 100;
  const totalAmount = amountAfterDiscount + gstAmount;
  
  // Product Table
  const tableStartY = saleData.customerPhone || saleData.customerEmail ? 125 : 115;
  
  // Table Header
  doc.setFillColor(240, 240, 240);
  doc.rect(20, tableStartY, 170, 12, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.rect(20, tableStartY, 170, 12);
  
  // Column Headers
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text('Description', 25, tableStartY + 7);
  doc.text('HSN', 85, tableStartY + 7);
  doc.text('Qty', 105, tableStartY + 7);
  doc.text('Rate', 120, tableStartY + 7);
  doc.text('Amount', 145, tableStartY + 7);
  doc.text('GST%', 170, tableStartY + 7);
  
  // Product Row
  const rowY = tableStartY + 20;
  doc.rect(20, tableStartY + 12, 170, 18);
  
  const productDescription = saleData.productName 
    ? `${saleData.productName}${saleData.imeiNumber ? ` (IMEI: ${saleData.imeiNumber})` : ''}`
    : 'Product/Service';
  
  // Split long product names into multiple lines if needed
  const maxWidth = 55;
  const splitDescription = doc.splitTextToSize(productDescription, maxWidth);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(splitDescription, 25, rowY + 2);
  doc.text(BUSINESS_CONFIG.gst.hsnCode, 85, rowY + 6);
  doc.text('-', 105, rowY + 6);
  doc.text(`₹${baseAmount.toFixed(2)}`, 120, rowY + 6);
  doc.text(`₹${amountAfterDiscount.toFixed(2)}`, 145, rowY + 6);
  doc.text(`IGST ${gstRate}%`, 170, rowY + 6);
  
  // Totals Section
  const totalsStartY = tableStartY + 40;
  const totalsWidth = 70;
  const totalsX = 120;
  
  // Calculate total section height based on whether discount exists
  const totalsSectionHeight = discount > 0 ? 40 : 30;
  
  doc.setFillColor(250, 250, 250);
  doc.rect(totalsX, totalsStartY, totalsWidth, totalsSectionHeight, 'F');
  doc.rect(totalsX, totalsStartY, totalsWidth, totalsSectionHeight);
  
  // Add border lines inside totals box
  doc.setDrawColor(200, 200, 200);
  if (discount > 0) {
    doc.line(totalsX, totalsStartY + 15, totalsX + totalsWidth, totalsStartY + 15);
    doc.line(totalsX, totalsStartY + 22, totalsX + totalsWidth, totalsStartY + 22);
    doc.line(totalsX, totalsStartY + 29, totalsX + totalsWidth, totalsStartY + 29);
  } else {
    doc.line(totalsX, totalsStartY + 15, totalsX + totalsWidth, totalsStartY + 15);
  }
  doc.setDrawColor(0, 0, 0);
  
  // Totals Content
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  let currentY = totalsStartY + 8;
  
  doc.text('Subtotal:', totalsX + 5, currentY);
  doc.text(`₹${baseAmount.toFixed(2)}`, totalsX + totalsWidth - 5, currentY, { align: 'right' });
  
  if (discount > 0) {
    currentY += 7;
    doc.text('Discount:', totalsX + 5, currentY);
    doc.text(`-₹${discount.toFixed(2)}`, totalsX + totalsWidth - 5, currentY, { align: 'right' });
    
    currentY += 7;
    doc.text('After Discount:', totalsX + 5, currentY);
    doc.text(`₹${amountAfterDiscount.toFixed(2)}`, totalsX + totalsWidth - 5, currentY, { align: 'right' });
  } else {
    // If no discount, show the amount as is
    currentY += 7;
    doc.text('Amount:', totalsX + 5, currentY);
    doc.text(`₹${amountAfterDiscount.toFixed(2)}`, totalsX + totalsWidth - 5, currentY, { align: 'right' });
  }
  
  currentY += 7;
  doc.text(`IGST (${gstRate}%):`, totalsX + 5, currentY);
  doc.text(`₹${gstAmount.toFixed(2)}`, totalsX + totalsWidth - 5, currentY, { align: 'right' });
  
  currentY += 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Total Amount:', totalsX + 5, currentY);
  doc.text(`₹${totalAmount.toFixed(2)}`, totalsX + totalsWidth - 5, currentY, { align: 'right' });
  
  // Amount in Words
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  const amountInWordsY = totalsStartY + totalsSectionHeight + 10;
  doc.text(`Amount in Words: ${numberToWords(Math.round(totalAmount))} Rupees Only`, 20, amountInWordsY);
  
  // Payment Information
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Payment Method: ${saleData.paymentMethod}`, 20, amountInWordsY + 10);
  
  // Terms and Conditions
  const termsY = amountInWordsY + 25;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Terms & Conditions:', 20, termsY);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('1. Goods once sold will not be taken back or exchanged.', 20, termsY + 7);
  doc.text('2. Warranty as per manufacturer terms and conditions.', 20, termsY + 14);
  doc.text('3. All disputes are subject to Ludhiana jurisdiction only.', 20, termsY + 21);
  doc.text('4. GST as applicable on the date of supply.', 20, termsY + 28);
  
  // Bank Details (if configured)
  if (BUSINESS_CONFIG.bank.accountNumber !== 'XXXXXXXXXXXX') {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Bank Details:', 20, termsY + 40);
    doc.setFont('helvetica', 'normal');
    doc.text(`Bank: ${BUSINESS_CONFIG.bank.name}`, 20, termsY + 47);
    doc.text(`A/c No: ${BUSINESS_CONFIG.bank.accountNumber}`, 20, termsY + 54);
    doc.text(`IFSC: ${BUSINESS_CONFIG.bank.ifsc}`, 20, termsY + 61);
  }
  
  // Signature Section
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('For ' + BUSINESS_CONFIG.name, 140, termsY + 25);
  doc.text('Authorized Signatory', 140, termsY + 40);
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text('Thank you for your business!', 20, 280);
  doc.text('This is a computer generated invoice.', 20, 285);
  
  // Generate filename
  const filename = `Invoice_${saleData.invoiceNumber || Date.now()}_${new Date().toISOString().split('T')[0]}.pdf`;
  
  // Save to Firebase Storage if userId provided
  if (userId) {
    try {
      const pdfBlob = doc.output('blob');
      const storageRef = ref(storage, `invoices/${userId}/sales/${filename}`);
      await uploadBytes(storageRef, pdfBlob);
      const downloadUrl = await getDownloadURL(storageRef);
      
      // Save the PDF locally as well
      doc.save(filename);
      
      return { filename, downloadUrl };
    } catch (error) {
      console.error('Error saving invoice to storage:', error);
      // Still save locally even if storage fails
      doc.save(filename);
      return { filename, downloadUrl: null };
    }
  } else {
    // Save the PDF locally only
    doc.save(filename);
    return { filename, downloadUrl: null };
  }
};

// Helper function to convert number to words (basic implementation)
const numberToWords = (num) => {
  if (num === 0) return 'Zero';
  
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  
  const convertHundreds = (n) => {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    } else if (n >= 10) {
      result += teens[n - 10] + ' ';
      return result;
    }
    if (n > 0) {
      result += ones[n] + ' ';
    }
    return result;
  };
  
  if (num >= 10000000) { // Crore
    return convertHundreds(Math.floor(num / 10000000)) + 'Crore ' + numberToWords(num % 10000000);
  } else if (num >= 100000) { // Lakh
    return convertHundreds(Math.floor(num / 100000)) + 'Lakh ' + numberToWords(num % 100000);
  } else if (num >= 1000) { // Thousand
    return convertHundreds(Math.floor(num / 1000)) + 'Thousand ' + numberToWords(num % 1000);
  } else {
    return convertHundreds(num);
  }
};